import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin
);

interface BarChartProps {
  data: Array<{ x: string | number; y: number }>;
  xTitle: string;
  yTitle: string;
}

const BarChart: React.FC<BarChartProps> = ({ data, xTitle, yTitle }) => {
  // Prepare chart data
  const chartData: ChartData<'bar'> = {
    labels: data.map(item => String(item.x)),
    datasets: [
      {
        label: yTitle,
        data: data.map(item => item.y),
        backgroundColor: 'rgba(54, 162, 235, 0.5)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      },
    ],
  };

  // Chart options
  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: `${yTitle} by ${xTitle}`,
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${yTitle}: ${context.parsed.y}`;
          }
        }
      },
      zoom: {
        pan: {
          enabled: true,
          mode: 'x',
          modifierKey: 'ctrl',
        },
        zoom: {
          wheel: {
            enabled: true,
            modifierKey: 'ctrl',
          },
          pinch: {
            enabled: true
          },
          mode: 'x',
          drag: {
            enabled: true,
            backgroundColor: 'rgba(54, 162, 235, 0.1)',
            borderColor: 'rgba(54, 162, 235, 0.3)',
            borderWidth: 1
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: xTitle
        },
        min: 0,
        max: Math.min(10, data.length - 1), // Show first 10 items by default
      },
      y: {
        title: {
          display: true,
          text: yTitle
        },
        beginAtZero: true
      }
    }
  };

  return (
    <div style={{ height: '400px', width: '100%', position: 'relative' }}>
      <div style={{ 
        position: 'absolute', 
        top: 10, 
        right: 10, 
        zIndex: 1,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        padding: '5px',
        borderRadius: '4px',
        fontSize: '12px'
      }}>
        Hold Ctrl + Scroll to zoom
        <br />
        Hold Ctrl + Drag to pan
      </div>
      <Bar data={chartData} options={options} />
    </div>
  );
};

export default BarChart; 